import React, { useState } from 'react';
import { Modal, Button, Table, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getSocialFeeDisplayRate } from '../enums/socialFeeRateMapping.enum';
import { formatAmountWithLocale } from '../helper/common.helper';
import './calculation-breakdown-modal.scss';

interface CalculationBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  calculationData: any;
  currency?: string;
}

const CalculationBreakdownModal: React.FC<CalculationBreakdownModalProps> = ({
  isOpen,
  onClose,
  calculationData,
  currency = 'SEK'
}) => {
  const { t, i18n } = useTranslation();
  
  // The API response structure is directly in calculationData
  const data = calculationData?.response || null;
  
  // Determine if we should use SEK values (when currency is not SEK)
  const isForeignCurrency = data?.totalCalculation?.currencyName && data.totalCalculation.currencyName !== 'SEK';
  
  // Format currency - always show SEK when foreign currency
  const formatCurrency = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, 'SEK', i18n.language);
    }
    return formatAmountWithLocale(amount, 'SEK', i18n.language);
  };
  
  // Format original currency (for Invoice Amount and VAT fields)
  const formatCurrencyOriginal = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, currency, i18n.language);
    }
    return formatAmountWithLocale(amount, currency, i18n.language);
  };
  
  // Helper to get SEK amount
  const getAmount = (directAmount: number, sekAmount: number) => {
    return isForeignCurrency ? sekAmount : directAmount;
  };

  // Render single freelancer view (structured with sections)
  const renderSingleFreelancerView = (calcData: any) => (
    <div className="calculation-breakdown-single">
      <div className="breakdown-section">
        {/* Step 1: Invoice amount inc. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
          <span className="item-value">{formatCurrencyOriginal(calcData.amountIncludingVat)}</span>
        </div>

        {/* Step 2: RUT/ROT Deduction (if applicable) */}
        {calcData.rutDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
            <span className="item-value">-{formatCurrency(calcData.rutDeductionAmount)}</span>
          </div>
        )}

        {calcData.rootDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
            <span className="item-value">-{formatCurrency(calcData.rootDeductionAmount)}</span>
          </div>
        )}

        {/* Step 3: Customer will pay (if RUT/ROT deduction exists) */}
        {(calcData.rutDeductionAmount > 0 || calcData.rootDeductionAmount > 0) && (
          <div className="calculation-item">
            <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
            <span className="item-value">{formatCurrency(calcData.amountIncludingVatAfterHouseholdServiceDeduction)}</span>
          </div>
        )}

        {/* Step 4: VAT */}
        {calcData.vat > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('VAT') || 'VAT'} ({calcData.vatPercentage}%)</span>
            <span className="item-value">{formatCurrencyOriginal(calcData.vat)}</span>
          </div>
        )}

        {/* Step 5: Invoice amount ex. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
          <span className="item-value">{formatCurrency(getAmount(calcData.amount, calcData.amountInSek || calcData.amount))}</span>
        </div>

        {/* Step 6: Invozio percent fee */}
        {calcData.fee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({calcData.fakturaFeePercentage}%)</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.fee, calcData.feeInSEK || calcData.fee))}</span>
          </div>
        )}

        {/* Step 7: Loyalty discount on invozio fee */}
        {calcData.loyaltyDiscountPercentage > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
            <span className="item-value">{calcData.loyaltyDiscountPercentage}%</span>
          </div>
        )}

        {/* Step 8: Social Fee */}
        {calcData.socialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">
              {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(calcData.socialFeeRateDeduction).toFixed(2)}%)
            </span>
            <span className="item-value">{formatCurrency(getAmount(calcData.socialFee, calcData.socialFeeInSek || calcData.socialFee))}</span>
          </div>
        )}

        {/* Step 9: Pension Social Fee */}
        {calcData.pensionSocialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'} ({calcData.pensionFeePer}%)</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.pensionSocialFee, calcData.pensionSocialFeeInSek || calcData.pensionSocialFee))}</span>
          </div>
        )}

        {/* Step 10: Pension */}
        {calcData.pension > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION') || 'Pension'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.pension, calcData.pensionInSek || calcData.pension))}</span>
          </div>
        )}

        {/* Step 11: Gross salary incl. holiday pay */}
        <div className="calculation-item">
          <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(calcData.gross, calcData.grossInSek || calcData.gross))}</span>
        </div>

        {/* Step 12: Tax */}
        {calcData.tax > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INCOME_TAX') || 'Tax'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.tax, calcData.taxInSek || calcData.tax))}</span>
          </div>
        )}

        {/* Step 13: Holiday Pay */}
        {calcData.holidayPayAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.holidayPayAmount, calcData.holidayPayAmountInSek || calcData.holidayPayAmount))}</span>
          </div>
        )}

        {/* Step 14: Express Fee */}
        {calcData.expressFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.expressFee, calcData.expressFeeInSek || calcData.expressFee))}</span>
          </div>
        )}

        {/* Step 15: Total Working Hours */}
        {calcData.workingHours && calcData.workingHours.length > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
            <span className="item-value">{calcData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} {t('HOURS') || 'Hours'}</span>
          </div>
        )}

        {/* Step 16: Tax-free articles */}
        {calcData.mileageTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.mileageTaxFreeAmount, calcData.mileageTaxFreeAmountInSek || calcData.mileageTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.expenditureForMaterialsAndToolsTaxFreeAmount, calcData.expenditureForMaterialsAndToolsTaxFreeAmountInSek || calcData.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.allowanceAbroadTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.allowanceAbroadTaxFreeAmount, calcData.allowanceAbroadTaxFreeAmountInSek || calcData.allowanceAbroadTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.domesticAllowanceFullDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.domesticAllowanceFullDayTaxFreeAmount, calcData.domesticAllowanceFullDayTaxFreeAmountInSek || calcData.domesticAllowanceFullDayTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.domesticAllowanceHalfDayTaxFreeAmount, calcData.domesticAllowanceHalfDayTaxFreeAmountInSek || calcData.domesticAllowanceHalfDayTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.travelCostsAccordingTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.travelCostsAccordingTaxFreeAmount, calcData.travelCostsAccordingTaxFreeAmountInSek || calcData.travelCostsAccordingTaxFreeAmount))}</span>
          </div>
        )}

        {calcData.allowance > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE') || 'Other Allowances'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.allowance, calcData.allowanceInSek || calcData.allowance))}</span>
          </div>
        )}

        {calcData.expense > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.expense, calcData.expenseInSek || calcData.expense))}</span>
          </div>
        )}

        {calcData.administratorFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(calcData.administratorFee, calcData.administratorFeeInSek || calcData.administratorFee))}</span>
          </div>
        )}
      </div>

      {/* Step 17: Net salary incl. holiday pay */}
      <div className="breakdown-section net-salary-section">
        <div className="calculation-item">
          <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(calcData.net, calcData.netInSek || calcData.net))}</span>
        </div>
      </div>
    </div>
  );

  // Render multiple freelancers view (Invoice Summary + Individual Breakdowns)
  const renderMultipleFreelancersView = (totalCalc: any, freelancersData: any[]) => (
    <div className="calculation-breakdown-multiple">
      {/* Invoice Summary Section */}
      <div className="breakdown-section">
        <h6 className="section-title">{t('INVOICE_SUMMARY') || 'Invoice Summary'}</h6>
        
        {/* Step 1: Invoice amount inc. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
          <span className="item-value">{formatCurrencyOriginal(totalCalc.amountIncludingVat)}</span>
        </div>

        {/* Step 2: RUT/ROT Deduction */}
        {totalCalc.rutDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
            <span className="item-value">-{formatCurrency(totalCalc.rutDeductionAmount)}</span>
          </div>
        )}

        {totalCalc.rootDeductionAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
            <span className="item-value">-{formatCurrency(totalCalc.rootDeductionAmount)}</span>
          </div>
        )}

        {/* Step 3: Customer will pay */}
        {(totalCalc.rutDeductionAmount > 0 || totalCalc.rootDeductionAmount > 0) && (
          <div className="calculation-item">
            <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
            <span className="item-value">{formatCurrency(totalCalc.amountIncludingVatAfterHouseholdServiceDeduction)}</span>
          </div>
        )}

        {/* Step 4: VAT */}
        {totalCalc.vat > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('VAT') || 'VAT'} ({totalCalc.vatPercentage}%)</span>
            <span className="item-value">{formatCurrencyOriginal(totalCalc.vat)}</span>
          </div>
        )}

        {/* Step 5: Invoice amount ex. VAT */}
        <div className="calculation-item">
          <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
          <span className="item-value">{formatCurrency(getAmount(totalCalc.amount, totalCalc.amountInSek || totalCalc.amount))}</span>
        </div>

        {/* Step 6: Invozio percent fee */}
        {totalCalc.fee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({totalCalc.fakturaFeePercentage}%)</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.fee, totalCalc.feeInSEK || totalCalc.fee))}</span>
          </div>
        )}

        {/* Step 7: Loyalty discount on invozio fee */}
        {totalCalc.loyaltyDiscountPercentage > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
            <span className="item-value">{totalCalc.loyaltyDiscountPercentage}%</span>
          </div>
        )}

        {/* Step 8: Social Fee */}
        {totalCalc.socialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">
              {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(totalCalc.socialFeeRateDeduction).toFixed(2)}%)
            </span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.socialFee, totalCalc.socialFeeInSek || totalCalc.socialFee))}</span>
          </div>
        )}

        {/* Step 9: Pension Social Fee */}
        {totalCalc.pensionSocialFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.pensionSocialFee, totalCalc.pensionSocialFeeInSek || totalCalc.pensionSocialFee))}</span>
          </div>
        )}

        {/* Step 10: Pension */}
        {totalCalc.pension > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('PENSION') || 'Pension'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.pension, totalCalc.pensionInSek || totalCalc.pension))}</span>
          </div>
        )}

        {/* Step 11: Gross salary incl. holiday pay */}
        <div className="calculation-item">
          <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(totalCalc.gross, totalCalc.grossInSek || totalCalc.gross))}</span>
        </div>

        {/* Step 12: Tax */}
        {totalCalc.tax > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('INCOME_TAX') || 'Tax'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.tax, totalCalc.taxInSek || totalCalc.tax))}</span>
          </div>
        )}

        {/* Step 13: Holiday Pay */}
        {totalCalc.holidayPayAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.holidayPayAmount, totalCalc.holidayPayAmountInSek || totalCalc.holidayPayAmount))}</span>
          </div>
        )}

        {/* Step 14: Express Fee */}
        {totalCalc.expressFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.expressFee, totalCalc.expressFeeInSek || totalCalc.expressFee))}</span>
          </div>
        )}

        {/* Step 15: Tax-free articles */}
        {totalCalc.mileageTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.mileageTaxFreeAmount, totalCalc.mileageTaxFreeAmountInSek || totalCalc.mileageTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.expenditureForMaterialsAndToolsTaxFreeAmount, totalCalc.expenditureForMaterialsAndToolsTaxFreeAmountInSek || totalCalc.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.allowanceAbroadTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.allowanceAbroadTaxFreeAmount, totalCalc.allowanceAbroadTaxFreeAmountInSek || totalCalc.allowanceAbroadTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.domesticAllowanceFullDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.domesticAllowanceFullDayTaxFreeAmount, totalCalc.domesticAllowanceFullDayTaxFreeAmountInSek || totalCalc.domesticAllowanceFullDayTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.domesticAllowanceHalfDayTaxFreeAmount, totalCalc.domesticAllowanceHalfDayTaxFreeAmountInSek || totalCalc.domesticAllowanceHalfDayTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.travelCostsAccordingTaxFreeAmount > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.travelCostsAccordingTaxFreeAmount, totalCalc.travelCostsAccordingTaxFreeAmountInSek || totalCalc.travelCostsAccordingTaxFreeAmount))}</span>
          </div>
        )}

        {totalCalc.expense > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.expense, totalCalc.expenseInSek || totalCalc.expense))}</span>
          </div>
        )}

        {totalCalc.administratorFee > 0 && (
          <div className="calculation-item">
            <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
            <span className="item-value">{formatCurrency(getAmount(totalCalc.administratorFee, totalCalc.administratorFeeInSek || totalCalc.administratorFee))}</span>
          </div>
        )}
      </div>

      {/* Step 16: Net salary incl. holiday pay */}
      <div className="borderTopCheck"></div>
      <div className="breakdown-section net-salary-section">
        <div className="calculation-item">
          <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
          <span className="item-value">{formatCurrency(getAmount(totalCalc.net, totalCalc.netInSek || totalCalc.net))}</span>
        </div>
      </div>
      <div className="borderBottomCheck"></div>

      {/* Individual Freelancer Breakdowns - Following calculation order */}
      {freelancersData.map((freelancerData: any, index: number) => (
        <div key={index} className="freelancer-breakdown">
          <div className="freelancer-name-header">
            <h6>{freelancerData.freelancerName || `Freelancer ${index + 1}`}</h6>
          </div>

          <div className="breakdown-subsection">
            {/* Step 1: Invoice amount inc. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
              <span className="item-value">{formatCurrencyOriginal(freelancerData.amountIncludingVat)}</span>
            </div>

            {/* Step 2: RUT/ROT Deduction */}
            {freelancerData.rutDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
                <span className="item-value">-{formatCurrency(freelancerData.rutDeductionAmount)}</span>
              </div>
            )}

            {freelancerData.rootDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
                <span className="item-value">-{formatCurrency(freelancerData.rootDeductionAmount)}</span>
              </div>
            )}

            {/* Step 3: Customer will pay */}
            {(freelancerData.rutDeductionAmount > 0 || freelancerData.rootDeductionAmount > 0) && (
              <div className="calculation-item">
                <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
                <span className="item-value">{formatCurrency(freelancerData.amountIncludingVatAfterHouseholdServiceDeduction)}</span>
              </div>
            )}

            {/* Step 4: VAT */}
            {freelancerData.vat > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('VAT') || 'VAT'} ({freelancerData.vatPercentage}%)</span>
                <span className="item-value">{formatCurrencyOriginal(freelancerData.vat)}</span>
              </div>
            )}

            {/* Step 5: Invoice amount ex. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
              <span className="item-value">{formatCurrency(getAmount(freelancerData.amount, freelancerData.amountInSek || freelancerData.amount))}</span>
            </div>

            {/* Step 6: Invozio percent fee */}
            {freelancerData.fee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({freelancerData.fakturaFeePercentage}%)</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.fee, freelancerData.feeInSEK || freelancerData.fee))}</span>
              </div>
            )}

            {/* Step 7: Loyalty discount */}
            {freelancerData.loyaltyDiscountPercentage > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
                <span className="item-value">{freelancerData.loyaltyDiscountPercentage}%</span>
              </div>
            )}

            {/* Step 8: Social Fee */}
            {freelancerData.socialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">
                  {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(freelancerData.socialFeeRateDeduction).toFixed(2)}%)
                </span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.socialFee, freelancerData.socialFeeInSek || freelancerData.socialFee))}</span>
              </div>
            )}

            {/* Step 9: Pension Social Fee */}
            {freelancerData.pensionSocialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'} ({freelancerData.pensionFeePer}%)</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.pensionSocialFee, freelancerData.pensionSocialFeeInSek || freelancerData.pensionSocialFee))}</span>
              </div>
            )}

            {/* Step 10: Pension */}
            {freelancerData.pension > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION') || 'Pension'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.pension, freelancerData.pensionInSek || freelancerData.pension))}</span>
              </div>
            )}

            {/* Step 11: Gross salary incl. holiday pay */}
            <div className="calculation-item">
              <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
              <span className="item-value">{formatCurrency(getAmount(freelancerData.gross, freelancerData.grossInSek || freelancerData.gross))}</span>
            </div>

            {/* Step 12: Tax */}
            {freelancerData.tax > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INCOME_TAX') || 'Tax'} ({freelancerData.taxPercentage ?? freelancerData.taxRateDeduction ?? '30.00'}%)</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.tax, freelancerData.taxInSek || freelancerData.tax))}</span>
              </div>
            )}

            {/* Step 13: Holiday Pay */}
            {freelancerData.holidayPayAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.holidayPayAmount, freelancerData.holidayPayAmountInSek || freelancerData.holidayPayAmount))}</span>
              </div>
            )}

            {/* Step 14: Express Fee */}
            {freelancerData.expressFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.expressFee, freelancerData.expressFeeInSek || freelancerData.expressFee))}</span>
              </div>
            )}

            {/* Step 15: Total Working Hours */}
            {freelancerData.workingHours && freelancerData.workingHours.length > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
                <span className="item-value">{freelancerData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} {t('HOURS') || 'Hours'}</span>
              </div>
            )}

            {/* Step 16: Tax-free articles */}
            {freelancerData.mileageTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.mileageTaxFreeAmount, freelancerData.mileageTaxFreeAmountInSek || freelancerData.mileageTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.expenditureForMaterialsAndToolsTaxFreeAmount, freelancerData.expenditureForMaterialsAndToolsTaxFreeAmountInSek || freelancerData.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.allowanceAbroadTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.allowanceAbroadTaxFreeAmount, freelancerData.allowanceAbroadTaxFreeAmountInSek || freelancerData.allowanceAbroadTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.domesticAllowanceFullDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.domesticAllowanceFullDayTaxFreeAmount, freelancerData.domesticAllowanceFullDayTaxFreeAmountInSek || freelancerData.domesticAllowanceFullDayTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.domesticAllowanceHalfDayTaxFreeAmount, freelancerData.domesticAllowanceHalfDayTaxFreeAmountInSek || freelancerData.domesticAllowanceHalfDayTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.travelCostsAccordingTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.travelCostsAccordingTaxFreeAmount, freelancerData.travelCostsAccordingTaxFreeAmountInSek || freelancerData.travelCostsAccordingTaxFreeAmount))}</span>
              </div>
            )}

            {freelancerData.allowance > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE') || 'Other Allowances'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.allowance, freelancerData.allowanceInSek || freelancerData.allowance))}</span>
              </div>
            )}

            {freelancerData.expense > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.expense, freelancerData.expenseInSek || freelancerData.expense))}</span>
              </div>
            )}

            {freelancerData.administratorFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
                <span className="item-value">{formatCurrency(getAmount(freelancerData.administratorFee, freelancerData.administratorFeeInSek || freelancerData.administratorFee))}</span>
              </div>
            )}
          </div>

          {/* Step 17: Net salary incl. holiday pay */}
          <div className="breakdown-subsection net-salary-subsection">
            <div className="calculation-item">
              <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
              <span className="item-value">{formatCurrency(getAmount(freelancerData.net, freelancerData.netInSek || freelancerData.net))}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCalculationData = (calcData: any, index?: number) => (
    <div className="calculation-list" key={index}>
      {index !== undefined && (
        <div className="freelancer-header">
          <h5><b>Freelancer {index + 1} (Name: {calcData.freelancerName})</b></h5>
        </div>
      )}
      
      <div className="calculation-item">
        <span className="item-label">{t('INVOICE_AMOUNT_INC_VAT') || 'Invoice amount inc. VAT'}</span>
        <span className="item-value">{formatCurrencyOriginal(calcData.amountIncludingVat)}</span>
      </div>

      {calcData.vat > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('VAT') || 'VAT'} ({calcData.vatPercentage}%)</span>
          <span className="item-value">{formatCurrencyOriginal(calcData.vat)}</span>
        </div>
      )}

      <div className="calculation-item">
        <span className="item-label">{t('INVOICE_AMOUNT_EX_VAT') || 'Invoice amount ex. VAT'}</span>
        <span className="item-value">{formatCurrency(calcData.amount)}</span>
      </div>

      {calcData.fee > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('INVOZIO_PERCENT_FEE') || 'Invozio percent fee'} ({calcData.fakturaFeePercentage}%)</span>
          <span className="item-value">{formatCurrency(calcData.fee)}</span>
        </div>
      )}

      {calcData.socialFee > 0 && (
        <div className="calculation-item">
          <span className="item-label">
            {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(calcData.socialFeeRateDeduction).toFixed(2)}%)
          </span>
          <span className="item-value">{formatCurrency(calcData.socialFee)}</span>
        </div>
      )}

      {calcData.pensionSocialFee > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'}</span>
          <span className="item-value">{formatCurrency(calcData.pensionSocialFee)}</span>
        </div>
      )}

      {calcData.pension > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('PENSION') || 'Pension'}</span>
          <span className="item-value">{formatCurrency(calcData.pension)}</span>
        </div>
      )}

      <div className="calculation-item highlight">
        <span className="item-label">{t('GROSS_SALARY_INCL_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
        <span className="item-value">{formatCurrency(calcData.gross)}</span>
      </div>

      {calcData.tax > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('TAX') || 'Tax'} ({calcData.taxPercentage || '30.00'}%)</span>
          <span className="item-value">{formatCurrency(calcData.tax)}</span>
        </div>
      )}

      {calcData.holidayPayAmount > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
          <span className="item-value">{formatCurrency(calcData.holidayPayAmount)}</span>
        </div>
      )}

      {calcData.expressFee > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
          <span className="item-value">{formatCurrency(calcData.expressFee)}</span>
        </div>
      )}

      {calcData.workingHours && calcData.workingHours.length > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
          <span className="item-value">{calcData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} Hours</span>
        </div>
      )}

      <div className="calculation-item highlight">
        <span className="item-label">{t('NET_SALARY_INCL_HOLIDAY_PAY') || 'Net salary incl. holiday pay'}</span>
        <span className="item-value">{formatCurrency(calcData.net)}</span>
      </div>

      {calcData.toPay > 0 && (
        <div className="calculation-item">
          <span className="item-label">{t('TO_PAY') || 'To Pay'}</span>
          <span className="item-value">{formatCurrency(calcData.toPay)}</span>
        </div>
      )}
    </div>
  );

  // Check if we have multiple freelancers
  const hasMultipleFreelancers = data?.freelancersData && data.freelancersData.length > 1;

  return (
    <Modal 
      show={isOpen} 
      onHide={onClose} 
      size="lg" 
      centered
      className="calculation-breakdown-modal"
      dialogClassName="modal-90vh"
    >
      <Modal.Header closeButton>
        <Modal.Title className="modal-title-text">
          {t('HOW_WE_CALCULATE_YOUR_INVOICE') || 'How We Calculate Your Invoice'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {data ? (
          <div className="breakdown-content">
            {hasMultipleFreelancers ? (
              // Multiple freelancers: Show Invoice Summary + Individual Breakdowns
              data.totalCalculation && data.freelancersData ? (
                renderMultipleFreelancersView(data.totalCalculation, data.freelancersData)
              ) : (
                <div className="no-data">
                  <h5>{t('NO_DATA_AVAILABLE') || 'No data available'}</h5>
                </div>
              )
            ) : (
              // Single freelancer: Show structured breakdown
              data.totalCalculation ? (
                renderSingleFreelancerView(data.totalCalculation)
              ) : (
                <div className="no-data">
                  <h5>{t('NO_DATA_AVAILABLE') || 'No data available'}</h5>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="no-data">
            <h5>{t('NO_CALCULATION_DATA') || 'No calculation data available'}</h5>
            <p>{t('NO_CALCULATION_DATA_DESC') || 'Please ensure you have entered all required information and try again.'}</p>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          style={{
            backgroundColor: "#6750b8",
            borderRadius: "25px",
            boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
            display: "flex",
            padding: "10px 18px",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            alignSelf: "stretch",
            border: "none"
          }} 
          onClick={onClose}
        >
          {t('CLOSE') || 'Close'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CalculationBreakdownModal;